import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Table,
  Card,
  Space,
  Modal,
  message,
  Row,
  Col,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
  SearchOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { Resizable, ResizeCallbackData } from "react-resizable";
import "react-resizable/css/styles.css";
import CustomerService from "../services/customer.service";
import type {
  ValueAddedServiceOrder,
  ValueAddedServiceSearchParams,
  ElderInfo,
  ServiceType,
} from "../services/customer.service";
import StaffService from "../services/staff.service";
import type {
  StaffResponse,
  StaffSearchParams,
  StaffListResponse,
} from "../types/staff.types";

const { Option } = Select;

// 简化的服务人员类型定义，只包含必要字段
interface SimpleStaffMember {
  id: string;
  name: string;
  avatar: string;
  gender: "男" | "女";
  age: number;
  phone: string;
}

// 可调整列宽的表头组件
interface ResizableHeaderProps {
  width: number;
  onResize: (width: number) => void;
  children: React.ReactNode;
  [key: string]: any;
}

const ResizableHeader: React.FC<ResizableHeaderProps> = ({
  width,
  onResize,
  children,
  ...restProps
}) => {
  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={(_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        onResize(size.width);
      }}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps}>{children}</th>
    </Resizable>
  );
};

// 默认列宽配置
const DEFAULT_COLUMN_WIDTHS = {
  orderId: 80,
  customerName: 100,
  customerPhone: 100,
  serviceType: 100,
  productName: 100,
  productPrice: 60,
  paidTime: 120,
  elderInfo: 160,
  elderPhone: 100,
  elderAddress: 200,
  assignedVisitStaff: 100,
  assignedServiceStaff: 100,
  action: 120,
};

interface AssignFormValues {
  staffId: string;
}

// 性别转换函数
const formatGender = (gender: number): string => {
  switch (gender) {
    case 1:
      return "男";
    case 2:
      return "女";
    default:
      return "未知";
  }
};

// 价格格式化函数：分转元
const formatPrice = (priceFen: number): string => {
  return (priceFen / 100).toFixed(2);
};

// 时间格式化函数：显示年月日时分
const formatDateTime = (dateTime: string): string => {
  if (!dateTime) return "-";
  const date = new Date(dateTime);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ValueAddedServiceManagement: React.FC = () => {
  const [form] = Form.useForm<ValueAddedServiceSearchParams>();
  const [assignForm] = Form.useForm<AssignFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<ValueAddedServiceOrder[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [searchParams, setSearchParams] =
    useState<ValueAddedServiceSearchParams>({});
  const [assignModalVisible, setAssignModalVisible] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] =
    useState<ValueAddedServiceOrder | null>(null);
  const [availableStaff, setAvailableStaff] = useState<SimpleStaffMember[]>([]);
  const [staffLoading, setStaffLoading] = useState<boolean>(false);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [serviceTypesLoading, setServiceTypesLoading] =
    useState<boolean>(false);

  // 列宽度状态管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    DEFAULT_COLUMN_WIDTHS
  );

  // 处理列宽度调整
  const handleResize = (columnKey: string) => (width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: width,
    }));
  };

  // 加载服务类型数据
  const loadServiceTypes = async () => {
    try {
      setServiceTypesLoading(true);
      const response = await CustomerService.getServiceTypes();
      if (response.success && response.data) {
        const serviceTypesData = Array.isArray(response.data)
          ? response.data
          : [];
        setServiceTypes(serviceTypesData);
      } else {
        message.error("加载服务类型失败");
        setServiceTypes([]);
      }
    } catch (error) {
      console.error("加载服务类型失败:", error);
      message.error("加载服务类型失败");
      setServiceTypes([]);
    } finally {
      setServiceTypesLoading(false);
    }
  };

  // 初始化数据
  useEffect(() => {
    loadServiceTypes(); // 加载服务类型
    handleSearch(); // 加载订单数据
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载可用服务人员 - 调用员工管理的服务人员列表API
  const loadAvailableStaff = async () => {
    try {
      setStaffLoading(true);

      // 调用员工管理的服务人员列表API，获取所有在职的服务人员
      const searchParams: StaffSearchParams = {
        page: 1,
        limit: 20, // 获取最多10个员工供选择
        // 不添加其他筛选条件，获取所有可用的服务人员
      };

      const response: StaffListResponse =
        await StaffService.getServiceStaffList(searchParams);

      if (response.success && response.data && response.data.items) {
        // 将服务端返回的StaffResponse数据转换为简化的员工信息格式
        const staffItems = Array.isArray(response.data.items)
          ? response.data.items
          : [];
        const transformedStaff: SimpleStaffMember[] = staffItems.map(
          (staff: StaffResponse) => ({
            id: staff.id.toString(),
            name: staff.name || "",
            avatar: staff.avatar || "",
            gender: staff.gender === 1 ? "男" : "女",
            age: staff.age || 0,
            phone: staff.phone || "",
          })
        );

        setAvailableStaff(transformedStaff);
      } else {
        message.error("加载可用服务人员失败");
        setAvailableStaff([]);
      }
    } catch (error) {
      console.error("加载可用服务人员失败:", error);
      message.error("加载可用服务人员失败");
      setAvailableStaff([]);
    } finally {
      setStaffLoading(false);
    }
  };

  // 搜索功能
  const handleSearch = async (values: ValueAddedServiceSearchParams = {}) => {
    setLoading(true);
    setSearchParams(values);

    try {
      const currentPagination = pagination || {
        current: 1,
        pageSize: 20,
        total: 0,
      };
      const searchParams = {
        ...values,
        page: currentPagination.current || 1,
        pageSize: currentPagination.pageSize || 20,
      };

      const response = await CustomerService.searchValueAddedServiceOrders(
        searchParams
      );

      if (response.success && response.data) {
        // 服务端返回的结构：response.data = { items: [], meta: {} }
        const { items, meta } = response.data;

        // 确保数据是数组格式
        const responseData = Array.isArray(items) ? items : [];
        setDataSource(responseData);
        console.log("API返回的数据:", responseData);

        // 安全地处理分页信息
        if (meta) {
          setPagination((prev) => ({
            ...prev,
            current: meta.page || 1,
            pageSize: meta.limit || 20,
            total: meta.total || 0,
          }));
        } else {
          // 如果没有meta信息，设置默认分页
          setPagination((prev) => ({
            ...prev,
            current: 1,
            pageSize: 20,
            total: 0,
          }));
        }
      } else {
        message.error("搜索失败，请重试");
        console.error("API响应数据格式错误:", response);
        // 设置空数据和默认分页
        setDataSource([]);
        setPagination((prev) => ({
          ...prev,
          current: 1,
          pageSize: 20,
          total: 0,
        }));
      }
    } catch (error) {
      console.error("搜索错误:", error);
      message.error("搜索失败，请检查网络连接");
      // 出错时也要设置安全的默认值
      setDataSource([]);
      setPagination((prev) => ({
        ...prev,
        current: 1,
        pageSize: 20,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    handleSearch();
  };

  // 分页变化
  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);

    // 重新搜索，使用新的分页参数
    const newSearchParams = {
      ...searchParams,
      page: newPagination.current || 1,
      pageSize: newPagination.pageSize || 20,
    };

    handleSearch(newSearchParams);
  };

  // 显示分配服务人员模态框
  const showAssignModal = async (record: ValueAddedServiceOrder) => {
    setSelectedOrder(record);
    setAssignModalVisible(true);

    // 加载可用服务人员数据
    await loadAvailableStaff();
  };

  // 当可用服务人员数据加载完成且模态框打开时，设置表单初始值
  useEffect(() => {
    if (assignModalVisible && selectedOrder && availableStaff.length > 0) {
      assignForm.setFieldsValue({
        staffId: selectedOrder.assignedServiceStaff
          ? availableStaff.find(
              (s) => s.name === selectedOrder.assignedServiceStaff!.name
            )?.id
          : undefined,
      });
    }
  }, [assignModalVisible, selectedOrder, availableStaff, assignForm]);

  // 分配服务人员
  const handleAssignStaff = async () => {
    try {
      const values = await assignForm.validateFields();
      const selectedStaff = availableStaff.find(
        (staff) => staff.id === values.staffId
      );

      if (selectedStaff && selectedOrder) {
        const response = await CustomerService.assignValueAddedService({
          orderId: selectedOrder.orderId,
          staffId: parseInt(values.staffId),
        });

        if (response.success) {
          const elderInfoArray = Array.isArray(selectedOrder.elderInfo)
            ? selectedOrder.elderInfo
            : [];
          const elderNames = elderInfoArray
            .map((elder) => elder.name)
            .join("、");
          message.success(
            `成功为 ${elderNames} 的${selectedOrder.serviceType}分配服务人员 ${selectedStaff.name}`
          );

          // 更新数据源
          setDataSource((prev) =>
            prev.map((item) =>
              item.orderId === selectedOrder.orderId
                ? {
                    ...item,
                    assignedServiceStaff: response.data.assignedServiceStaff,
                  }
                : item
            )
          );

          setAssignModalVisible(false);
          assignForm.resetFields();
        } else {
          message.error(response.data.message || "分配失败");
        }
      }
    } catch (error) {
      console.error("分配服务人员失败:", error);
      message.error("分配失败，请重试");
    }
  };

  const columns: ColumnsType<ValueAddedServiceOrder> = [
    {
      title: "订单ID",
      dataIndex: "orderId",
      key: "orderId",
      width: columnWidths.orderId,
      onHeaderCell: () => ({
        width: columnWidths.orderId,
        onResize: handleResize("orderId"),
      }),
    },
    {
      title: "客户姓名",
      dataIndex: "customerName",
      key: "customerName",
      width: columnWidths.customerName,
      onHeaderCell: () => ({
        width: columnWidths.customerName,
        onResize: handleResize("customerName"),
      }),
    },
    {
      title: "客户手机号",
      dataIndex: "customerPhone",
      key: "customerPhone",
      width: columnWidths.customerPhone,
      onHeaderCell: () => ({
        width: columnWidths.customerPhone,
        onResize: handleResize("customerPhone"),
      }),
    },
    {
      title: "服务类型",
      dataIndex: "serviceType",
      key: "serviceType",
      width: columnWidths.serviceType,
      onHeaderCell: () => ({
        width: columnWidths.serviceType,
        onResize: handleResize("serviceType"),
      }),
    },
    {
      title: "产品名称",
      dataIndex: "productName",
      key: "productName",
      width: columnWidths.productName,
      onHeaderCell: () => ({
        width: columnWidths.productName,
        onResize: handleResize("productName"),
      }),
    },
    {
      title: "价格(元)",
      key: "productPrice",
      width: columnWidths.productPrice,
      onHeaderCell: () => ({
        width: columnWidths.productPrice,
        onResize: handleResize("productPrice"),
      }),
      render: (_, record) => {
        return `¥${formatPrice(record.productPriceFen)}`;
      },
    },
    {
      title: "支付时间",
      key: "paidTime",
      width: columnWidths.paidTime,
      onHeaderCell: () => ({
        width: columnWidths.paidTime,
        onResize: handleResize("paidTime"),
      }),
      render: (_, record) => {
        return formatDateTime(record.paidTime);
      },
    },
    {
      title: "老人信息",
      key: "elderInfo",
      width: columnWidths.elderInfo,
      onHeaderCell: () => ({
        width: columnWidths.elderInfo,
        onResize: handleResize("elderInfo"),
      }),
      render: (_, record) => {
        const elderInfoArray = Array.isArray(record.elderInfo)
          ? record.elderInfo
          : [];
        return elderInfoArray.map((elder: ElderInfo, index: number) => (
          <div
            key={index}
            style={{
              marginBottom: index < elderInfoArray.length - 1 ? "8px" : "0",
            }}
          >
            {`${elder.name}，${formatGender(elder.gender)}，${elder.age}岁，${
              elder.identityName
            }`}
          </div>
        ));
      },
    },
    {
      title: "老人手机号",
      dataIndex: "elderPhone",
      key: "elderPhone",
      width: columnWidths.elderPhone,
      onHeaderCell: () => ({
        width: columnWidths.elderPhone,
        onResize: handleResize("elderPhone"),
      }),
    },
    {
      title: "老人住址",
      dataIndex: "elderAddress",
      key: "elderAddress",
      width: columnWidths.elderAddress,
      ellipsis: true,
      onHeaderCell: () => ({
        width: columnWidths.elderAddress,
        onResize: handleResize("elderAddress"),
      }),
    },
    {
      title: "随访人员",
      dataIndex: "assignedVisitStaff",
      key: "assignedVisitStaff",
      width: columnWidths.assignedVisitStaff,
      onHeaderCell: () => ({
        width: columnWidths.assignedVisitStaff,
        onResize: handleResize("assignedVisitStaff"),
      }),
      render: (staff: ValueAddedServiceOrder["assignedVisitStaff"]) => {
        if (!staff) {
          return <span style={{ color: "#999" }}>未分配</span>;
        }
        return (
          <div>
            <div>{staff.name}</div>
            <div style={{ color: "#666", fontSize: "12px" }}>{staff.phone}</div>
          </div>
        );
      },
    },
    {
      title: "服务人员",
      dataIndex: "assignedServiceStaff",
      key: "assignedServiceStaff",
      width: columnWidths.assignedServiceStaff,
      onHeaderCell: () => ({
        width: columnWidths.assignedServiceStaff,
        onResize: handleResize("assignedServiceStaff"),
      }),
      render: (staff: ValueAddedServiceOrder["assignedServiceStaff"]) => {
        if (!staff) {
          return <span style={{ color: "#999" }}>未分配</span>;
        }
        return (
          <div>
            <div>{staff.name}</div>
            <div style={{ color: "#666", fontSize: "12px" }}>{staff.phone}</div>
          </div>
        );
      },
    },
    {
      title: "操作",
      key: "action",
      width: columnWidths.action,
      fixed: "right",
      onHeaderCell: () => ({
        width: columnWidths.action,
        onResize: handleResize("action"),
      }),
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => showAssignModal(record)}
          style={{ width: "100%" }}
        >
          分配服务
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Row gutter={20} style={{ width: "100%" }}>
            <Col span={4}>
              <Form.Item name="customerName" label="子女姓名">
                <Input placeholder="请输入客户姓名" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="elderName" label="老人姓名">
                <Input placeholder="请输入老人姓名" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="elderGender" label="老人性别">
                <Select placeholder="请选择老人性别" allowClear>
                  <Option value="male">男</Option>
                  <Option value="female">女</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="serviceTypeId" label="服务类型">
                <Select
                  placeholder="请选择服务类型"
                  allowClear
                  loading={serviceTypesLoading}
                  notFoundContent={
                    serviceTypesLoading ? "加载中..." : "暂无数据"
                  }
                >
                  {Array.isArray(serviceTypes) &&
                    serviceTypes.map((type) => (
                      <Option key={type.id} value={type.id}>
                        {type.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={20} style={{ width: "100%", marginTop: 16 }}>
            <Col span={4}>
              <Form.Item name="customerPhone" label="子女手机">
                <Input placeholder="请输入客户手机号" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="elderPhone" label="老人手机">
                <Input placeholder="请输入老人手机号" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="elderAddress" label="老人住址">
                <Input placeholder="请输入老人住址" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} style={{ width: "100%", marginTop: 16 }}>
            <Col span={18}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={loading}
                >
                  搜索
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 列表区域 */}
      <Card>
        <Table
          columns={columns}
          dataSource={Array.isArray(dataSource) ? dataSource : []}
          rowKey="orderId"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1440 }}
          components={{
            header: {
              cell: ResizableHeader,
            },
          }}
        />
      </Card>

      {/* 分配服务人员模态框 */}
      <Modal
        title="分配服务人员"
        open={assignModalVisible}
        onOk={handleAssignStaff}
        onCancel={() => {
          setAssignModalVisible(false);
          assignForm.resetFields();
        }}
        width={600}
      >
        {selectedOrder && (
          <div style={{ marginBottom: 16 }}>
            <p>
              <strong>订单ID：</strong>
              {selectedOrder.orderId}
            </p>
            <p>
              <strong>客户：</strong>
              {selectedOrder.customerName}， {selectedOrder.customerPhone}
            </p>
            <p>
              <strong>服务类型：</strong>
              {selectedOrder.serviceType}
            </p>
            <p>
              <strong>产品：</strong>
              {selectedOrder.productName} - ¥
              {formatPrice(selectedOrder.productPriceFen)}
            </p>
            <p>
              <strong>支付时间：</strong>
              {formatDateTime(selectedOrder.paidTime)}
            </p>

            {/* 老人信息分组 */}
            <div>
              <p>
                <strong>老人信息：</strong>
              </p>
              {Array.isArray(selectedOrder.elderInfo) &&
                selectedOrder.elderInfo.map((elder, index) => (
                  <div key={index} style={{ marginLeft: 16, marginBottom: 8 }}>
                    <p>
                      姓名：{elder.name} ( {selectedOrder.customerName}
                      {elder.identityName})
                    </p>
                    <p>
                      性别：{formatGender(elder.gender)}，年龄：{elder.age}岁
                    </p>
                  </div>
                ))}
              <p style={{ marginLeft: 16 }}>
                联系电话：
                {selectedOrder.elderPhone}
              </p>
              <p style={{ marginLeft: 16 }}>
                住址：
                {selectedOrder.elderAddress}
              </p>
            </div>

            {/* 关联随访人员信息 */}
            {selectedOrder.assignedVisitStaff && (
              <div style={{ marginTop: 16 }}>
                <p>
                  <strong>关联随访人员：</strong>
                </p>
                <div style={{ marginLeft: 16 }}>
                  <p>姓名：{selectedOrder.assignedVisitStaff.name}</p>
                  <p>手机：{selectedOrder.assignedVisitStaff.phone}</p>
                </div>
              </div>
            )}
          </div>
        )}
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="staffId"
            label="选择服务人员"
            rules={[{ required: true, message: "请选择服务人员" }]}
          >
            <Select
              placeholder="请选择服务人员"
              showSearch
              loading={staffLoading}
              notFoundContent={staffLoading ? "加载中..." : "暂无数据"}
            >
              {Array.isArray(availableStaff) &&
                availableStaff.map((staff) => (
                  <Option key={staff.id} value={staff.id}>
                    {staff.name} - {staff.phone} ({staff.gender}, {staff.age}岁)
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 自定义样式 */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .react-resizable-handle {
            position: absolute;
            width: 10px;
            height: 100%;
            bottom: 0;
            right: -5px;
            cursor: col-resize;
            z-index: 1;
          }

          .react-resizable-handle:hover {
            border-right: 2px solid #1890ff;
          }

          .ant-table-thead > tr > th {
            position: relative;
          }

          .ant-table-thead > tr > th .react-resizable-handle {
            background: transparent;
          }

          .ant-table-thead > tr > th .react-resizable-handle:hover {
            background: rgba(24, 144, 255, 0.1);
          }
          .ant-form-item-label {
            min-width: 80px;
          }
        `,
        }}
      />
    </div>
  );
};

export default ValueAddedServiceManagement;
